import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { authApi } from "@/lib/humhub-api/auth";
import type { ProfileUpdateRequest, User } from "@/lib/humhub-api/types";
import { usersApi } from "@/lib/humhub-api/users";

export const userQueryKeys = {
  all: ["users"] as const,
  current: () => [...userQueryKeys.all, "current"] as const,
  byId: (id: number) => [...userQueryKeys.all, "detail", id] as const,
  profile: () => [...userQueryKeys.all, "profile"] as const,
};

export const useUser = () => {
  const queryClient = useQueryClient();

  // 現在のユーザー情報を取得
  const {
    data: currentUser,
    isLoading,
    error,
    refetch: refetchCurrentUser,
  } = useQuery({
    queryKey: userQueryKeys.current(),
    queryFn: async () => {
      try {
        const userInfo = await authApi.getCurrentUser();
        return userInfo;
      } catch (err: unknown) {
        console.error("Error fetching current user:", err);
        throw new Error(
          typeof err === "object" && err !== null && "message" in err
            ? (err.message as string)
            : "Failed to load user information",
        );
      }
    },
    staleTime: 10 * 60 * 1000, // 10分間
    gcTime: 30 * 60 * 1000, // 30分間キャッシュ保持
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // ID指定でユーザー情報を取得（キャッシュ付き）
  const fetchUserById = useCallback(
    async (userId: number): Promise<User> => {
      // キャッシュから取得を試行
      const cachedUser = queryClient.getQueryData<User>(userQueryKeys.byId(userId));
      if (cachedUser) {
        return cachedUser;
      }

      // キャッシュにない場合はAPIから取得
      try {
        const userInfo = await usersApi.getUserById(userId);

        // 取得したデータをキャッシュに保存
        queryClient.setQueryData(userQueryKeys.byId(userId), userInfo);

        return userInfo;
      } catch (err: unknown) {
        console.error("Error fetching user by ID:", err);
        throw new Error(
          typeof err === "object" && err !== null && "message" in err
            ? (err.message as string)
            : "Failed to load user information",
        );
      }
    },
    [queryClient],
  );

  // プロフィール更新のmutation
  const updateProfileMutation = useMutation({
    mutationFn: async (updateData: ProfileUpdateRequest) => {
      if (!currentUser) {
        throw new Error("No current user to update");
      }

      const updatedUser = await usersApi.updateUserProfile(updateData);
      return updatedUser;
    },
    onSuccess: (updatedUser) => {
      // 現在のユーザー情報のキャッシュを更新
      queryClient.setQueryData(userQueryKeys.current(), updatedUser);

      // ID別キャッシュも更新
      if (updatedUser.id) {
        queryClient.setQueryData(userQueryKeys.byId(updatedUser.id), updatedUser);
      }
    },
    onError: (err: unknown) => {
      console.error("useUser: Error updating user profile:", err);
    },
  });

  // 現在のユーザー情報を再取得
  const refreshUser = useCallback(async () => {
    const result = await refetchCurrentUser();
    return result.data;
  }, [refetchCurrentUser]);

  // 現在のユーザー情報を手動で取得（互換性のため）
  const fetchCurrentUser = useCallback(async (): Promise<User> => {
    const result = await queryClient.fetchQuery({
      queryKey: userQueryKeys.current(),
      queryFn: async () => {
        const userInfo = await authApi.getCurrentUser();
        return userInfo;
      },
      staleTime: 10 * 60 * 1000,
    });
    return result;
  }, [queryClient]);

  // プロフィール更新の実行関数
  const updateUserProfile = useCallback(
    async (updateData: ProfileUpdateRequest) => {
      return updateProfileMutation.mutateAsync(updateData);
    },
    [updateProfileMutation],
  );

  // エラーメッセージの文字列化
  const errorMessage = error
    ? typeof error === "object" && error !== null && "message" in error
      ? (error.message as string)
      : "An error occurred"
    : null;

  return {
    // 状態
    currentUser: currentUser || null,
    isLoading,
    error: errorMessage,
    isUpdating: updateProfileMutation.isPending,

    // アクション
    fetchCurrentUser,
    fetchUserById,
    updateUserProfile,
    refreshUser,

    // 追加のTanStack Query機能
    invalidateCurrentUser: () =>
      queryClient.invalidateQueries({ queryKey: userQueryKeys.current() }),
    invalidateUserById: (userId: number) =>
      queryClient.invalidateQueries({ queryKey: userQueryKeys.byId(userId) }),
    invalidateAllUsers: () => queryClient.invalidateQueries({ queryKey: userQueryKeys.all }),

    // Mutation状態
    updateError: updateProfileMutation.error,
    updateSuccess: updateProfileMutation.isSuccess,
  };
};

// 特定ユーザーの情報を取得する専用フック
export const useUserById = (userId: number | null, enabled = true) => {
  return useQuery({
    queryKey: userQueryKeys.byId(userId ?? 0),
    queryFn: () => {
      if (userId === null) {
        throw new Error("User ID is null");
      }
      return usersApi.getUserById(userId);
    },
    enabled: enabled && userId !== null,
    staleTime: 5 * 60 * 1000, // 5分間
    gcTime: 10 * 60 * 1000, // 10分間キャッシュ保持
    retry: 2,
  });
};

// 複数ユーザーを一度に取得するフック
export const useUsersByIds = (userIds: number[], enabled = true) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [...userQueryKeys.all, "multiple", userIds.sort()],
    queryFn: async () => {
      const users = await Promise.all(
        userIds.map(async (id) => {
          // キャッシュから確認
          const cached = queryClient.getQueryData<User>(userQueryKeys.byId(id));
          if (cached) return cached;

          // キャッシュにない場合は取得
          const user = await usersApi.getUserById(id);
          queryClient.setQueryData(userQueryKeys.byId(id), user);
          return user;
        }),
      );
      return users;
    },
    enabled: enabled && userIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};
